import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import { Box, useMediaQuery } from '@mui/material';
import { fetchGroup } from '../ducks/group';
import { Configurator } from '../types/Configurator';
import { GroupMember } from '../types/GroupMember';
import { stringToLowerIncludes } from '../utils/stringUtils';
import { isIdeaRoomGroup } from '../utils/userUtils';
import { useAppDispatch, useAppSelector } from '../hooks';
import { GroupsComponentTabs } from './Groups';
import { configuratorsColumns, MUIDataGrid, MUIDataGridColumn } from '../constants/MUIDataGrid';
import { UsersTable } from './team/UsersTable';
import { MuiDataGrid } from './MUIDataGrid';
import { getDefaultColumnDefinition } from '../utils/MUIDataGridUtils';
import { ConfiguratorsCards } from './groups/ConfiguratorsCards';

interface Props {
  groupId: string;
  tab: GroupsComponentTabs;
}

export const Group: React.FC<Props> = ({ groupId, tab }: Props) => {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const dispatch = useAppDispatch();

  const [filteredGroupMembers, setFilteredGroupMembers] = useState<GroupMember[]>([]);
  const [filteredConfigurators, setFilteredConfigurators] = useState<Configurator[]>([]);
  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const { group, loading } = useAppSelector((state) => state.group);
  const searchTerm = useAppSelector((state) => state.search.searchTerm);

  // Effect that filters members by searchTerm anytime either members or searchTerm change
  useEffect(() => {
    if (group && group.members) {
      if (searchTerm.length === 0) {
        setFilteredGroupMembers(group.members);
        return;
      }
      const tests = [
        (groupMember: GroupMember): boolean => stringToLowerIncludes(groupMember.email, searchTerm),
        (groupMember: GroupMember): boolean => stringToLowerIncludes(groupMember.name, searchTerm),
      ];

      setFilteredGroupMembers(group.members.filter((groupMember) => tests.some((test) => test(groupMember))));
    } else {
      setFilteredGroupMembers([]);
    }
  }, [group, searchTerm]);

  // Effect that filters configurators by searchTerm anytime either configurators or searchTerm change
  useEffect(() => {
    if (group?.configurators) {
      const configurators = group.configurators.map((configurator) => ({
        ...configurator,
        ...(configurator?.vendorData?.vendor || {}),
      }));

      if (searchTerm.length === 0) {
        setFilteredConfigurators(configurators);
        return;
      }

      const tests = [
        (configurator: Configurator): boolean => stringToLowerIncludes(configurator.vendor, searchTerm),
        (configurator: Configurator): boolean => stringToLowerIncludes(configurator.key, searchTerm),
      ];

      setFilteredConfigurators(configurators.filter((configurator) => tests.some((test) => test(configurator))));
    } else {
      setFilteredConfigurators([]);
    }
  }, [group, searchTerm]);

  useEffect(() => {
    dispatch(fetchGroup(groupId));
  }, [groupId, dispatch]);

  const columns: GridColDef[] = useMemo(
    () =>
      configuratorsColumns.map((column: MUIDataGridColumn) =>
        getDefaultColumnDefinition(
          {
            table: MUIDataGrid.Configurators,
            editable: false,
            supplierOnly: column === MUIDataGridColumn.Pricing,
          },
          column,
          {},
          dispatch,
          t,
        ),
      ),
    [t, dispatch],
  );

  return (
    <Box height="100%" width="100%">
      {!isIdeaRoomGroup(groupId) && tab === GroupsComponentTabs.Configurators && !mobileView && (
        <MuiDataGrid
          loading={loading}
          rows={filteredConfigurators}
          columns={columns}
          apiRef={apiRef}
          showToolbar={false}
          getRowId={({ clientId }) => clientId}
        />
      )}
      {!isIdeaRoomGroup(groupId) && tab === GroupsComponentTabs.Configurators && mobileView && (
        <ConfiguratorsCards filteredConfigurators={filteredConfigurators} />
      )}
      {tab === GroupsComponentTabs.Members && (
        <UsersTable
          loading={loading}
          members={filteredGroupMembers}
          statsAndStatus
          table={MUIDataGrid.Users}
          showToolbar={false}
        />
      )}
    </Box>
  );
};
